import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ClipboardList, Plus, Trash2, Save, RotateCcw, GripVertical, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const STAGE_LABELS = {
  ordered: "Ordered",
  in_process: "In Process",
  awaiting_arrival: "Awaiting Materials",
  scheduling: "Scheduling",
  scheduled: "Scheduled",
  installed: "Installed",
  completed: "Completed",
};

const STAGE_HINTS = {
  ordered: "Contract + deposit + initial site info",
  in_process: "Survey, engineering, materials ordering",
  awaiting_arrival: "Shipment tracking & warehouse prep",
  scheduling: "Materials received, permit approved, date set",
  scheduled: "Crew briefed, tools & weather check",
  installed: "Install photos, commissioning, walkthrough",
  completed: "Warranty & punchlist closure",
};

export default function AdminProjectStages() {
  const [stages, setStages] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/project-stages");
      setStages(r.data || []);
      if (r.data?.length && !active) {
        setActive(r.data[0].stage);
        setItems(cloneItems(r.data[0].items));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!active) return;
    const found = stages.find((s) => s.stage === active);
    if (found) {
      setItems(cloneItems(found.items));
      setDirty(false);
    }
  }, [active, stages]);

  const cloneItems = (arr) => (arr || []).map((it) => ({ ...it }));

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    setDirty(true);
  };

  const addItem = () => {
    setItems((arr) => [
      ...arr,
      { key: `custom_${Date.now().toString(36)}`, label: "New checklist item", required: true },
    ]);
    setDirty(true);
  };

  const removeItem = (idx) => {
    setItems((arr) => arr.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const moveItem = (idx, dir) => {
    setItems((arr) => {
      const copy = [...arr];
      const t = idx + dir;
      if (t < 0 || t >= copy.length) return copy;
      [copy[idx], copy[t]] = [copy[t], copy[idx]];
      return copy;
    });
    setDirty(true);
  };

  const validate = () => {
    if (!items.length) return "At least one item required";
    const keys = new Set();
    for (const it of items) {
      if (!it.key?.trim() || !it.label?.trim()) return "Every item needs a key and a label";
      if (keys.has(it.key)) return `Duplicate key: ${it.key}`;
      keys.add(it.key);
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const r = await api.put(`/admin/project-stages/${active}`, { items });
      setStages((arr) => arr.map((s) => (s.stage === active ? { ...s, items: r.data.items } : s)));
      setDirty(false);
      toast.success(`${STAGE_LABELS[active]} template saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm(`Reset the "${STAGE_LABELS[active]}" checklist to built-in defaults?`)) return;
    try {
      const r = await api.post(`/admin/project-stages/${active}/reset`);
      setStages((arr) => arr.map((s) => (s.stage === active ? { ...s, items: r.data.items } : s)));
      setItems(cloneItems(r.data.items));
      setDirty(false);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const inputCls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm";

  return (
    <div data-testid="admin-project-stages" className="space-y-6">
      <Link to="/admin/settings" className="text-xs text-[#C9A356] inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to settings
      </Link>

      <div>
        <p className="overline">Configure</p>
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
          Stage Checklists
        </h1>
        <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
          Edit the checklist items required at each project stage. Templates apply to new
          projects and any project that hasn't reached that stage yet.
        </p>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Stage list */}
        <div className="card-pb p-2 h-fit">
          {stages.map((s) => {
            const isActive = s.stage === active;
            return (
              <button
                key={s.stage}
                onClick={() => {
                  if (dirty && !confirm("Discard unsaved changes?")) return;
                  setActive(s.stage);
                }}
                data-testid={`stage-tab-${s.stage}`}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                  isActive
                    ? "bg-[#0A2540] text-white"
                    : "text-[#0A2540] dark:text-white hover:bg-[#F8FAFC] dark:hover:bg-slate-800"
                }`}
              >
                <div className="font-medium">{STAGE_LABELS[s.stage] || s.stage}</div>
                <div className={`text-[10px] mt-0.5 ${isActive ? "text-[#C9A356]" : "text-[#94A3B8]"}`}>
                  {(s.items || []).length} item{(s.items || []).length === 1 ? "" : "s"}
                </div>
              </button>
            );
          })}
        </div>

        {/* Editor */}
        <div className="card-pb p-6">
          {!active ? (
            <div className="text-sm text-[#94A3B8]">Loading…</div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="inline-flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-[#C9A356]" />
                    <h2 className="font-heading text-xl text-[#0A2540] dark:text-white">
                      {STAGE_LABELS[active]}
                    </h2>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-1">{STAGE_HINTS[active]}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={reset}
                    className="text-xs px-3 py-1.5 border border-[#E2E8F0] rounded-md hover:bg-[#F8FAFC] dark:border-slate-700 dark:hover:bg-slate-800 inline-flex items-center gap-1"
                    data-testid="reset-stage-defaults"
                  >
                    <RotateCcw className="w-3 h-3" /> Defaults
                  </button>
                  <button
                    onClick={save}
                    disabled={!dirty || saving}
                    className="btn-accent text-sm inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="save-stage-template"
                  >
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-[24px_140px_1fr_100px_32px] gap-2 items-center border border-[#E2E8F0] dark:border-slate-700 rounded-md p-2"
                    data-testid={`stage-item-${idx}`}
                  >
                    <div className="flex flex-col">
                      <button
                        onClick={() => moveItem(idx, -1)}
                        disabled={idx === 0}
                        className="text-[10px] text-[#94A3B8] disabled:opacity-30 hover:text-[#0A2540]"
                        title="Move up"
                        data-testid={`item-up-${idx}`}
                      >▲</button>
                      <button
                        onClick={() => moveItem(idx, 1)}
                        disabled={idx === items.length - 1}
                        className="text-[10px] text-[#94A3B8] disabled:opacity-30 hover:text-[#0A2540]"
                        title="Move down"
                        data-testid={`item-down-${idx}`}
                      >▼</button>
                    </div>
                    <input
                      className={`${inputCls} font-mono text-xs`}
                      value={it.key}
                      onChange={(e) => updateItem(idx, { key: e.target.value })}
                      placeholder="unique_key"
                      data-testid={`item-key-${idx}`}
                    />
                    <input
                      className={inputCls}
                      value={it.label}
                      onChange={(e) => updateItem(idx, { label: e.target.value })}
                      placeholder="What must be done?"
                      data-testid={`item-label-${idx}`}
                    />
                    <label className="text-xs inline-flex items-center gap-1.5 text-[#475569] dark:text-slate-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!it.required}
                        onChange={(e) => updateItem(idx, { required: e.target.checked })}
                        data-testid={`item-required-${idx}`}
                      />
                      Required
                    </label>
                    <button
                      onClick={() => removeItem(idx)}
                      className="text-red-500 hover:text-red-700"
                      title="Remove"
                      data-testid={`item-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={addItem}
                className="mt-3 w-full text-sm text-[#C9A356] border border-dashed border-[#C9A356]/40 rounded-md py-2 hover:bg-[#C9A356]/5 inline-flex items-center justify-center gap-1"
                data-testid="add-stage-item"
              >
                <Plus className="w-4 h-4" /> Add checklist item
              </button>

              {dirty && (
                <p className="text-xs text-amber-600 mt-3 italic" data-testid="dirty-indicator">
                  Unsaved changes — click Save above.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
